import { useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { Calendar } from './Calendar';
import Parse, { ParseClasses } from '../api/config';
import { useAuth } from './AuthContext';
import { useApprovalQueue } from '../hooks/useApprovalQueue';
import {
    ApprovalCount,
    ApprovalRail,
    Button,
    Card,
    CardHeader,
    EmptyState,
    PageBody,
    PageHeader,
    StatusBadge,
    deriveStatus,
    isArchived,
    roleLabel,
} from './ui';

const formatEventDate = (value) => {
    if (!value) return 'No date set';
    // A bare YYYY-MM-DD parses as UTC and lands a day early in the US.
    const date = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T00:00:00`) : new Date(value);
    return Number.isNaN(date.getTime()) ? 'No date set' : format(date, 'EEE, MMM d');
};

const firstName = (name) => (name || '').split(' ')[0];

export const Dashboard = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { items: approvalQueue, loading: approvalsLoading } = useApprovalQueue();
    const [openRequests, setOpenRequests] = useState([]);

    const fetchOpenRequests = useCallback(async () => {
        if (!user?.email) return;
        try {
            const query = new Parse.Query(ParseClasses.Form);
            query.equalTo('owner', user.email);
            query.notEqualTo('isCancelled', true);
            query.notEqualTo('isFullyApproved', true);
            query.descending('createdAt');
            // Fetch more than we show: archived ones are dropped below, and
            // limiting first would let them eat the slots.
            query.limit(25);

            const results = await query.find();
            setOpenRequests(
                results
                    .map((form) => form.toJSON())
                    // Something that finished weeks ago isn't "in progress".
                    .filter((data) => !isArchived(data))
                    .slice(0, 5)
                    .map((data) => ({
                        id: data.objectId,
                        title: data.eventName || 'Untitled event',
                        eventDate: data.dayDateOfEvent,
                        occurrenceCount: (data.occurrenceDates || []).length,
                        status: deriveStatus(data),
                        requiredApprovals: data.requiredApprovals || [],
                        completedApprovals: data.completedApprovals || [],
                    }))
            );
        } catch (error) {
            console.error('Could not load your open requests:', error);
        }
    }, [user]);

    useEffect(() => {
        fetchOpenRequests();
    }, [fetchOpenRequests]);

    return (
        <>
            <PageHeader
                title={firstName(user?.name) ? `Hello, ${firstName(user.name)}` : 'Dashboard'}
                description="What needs you, what you're waiting on, and what's on the campus calendar."
                actions={
                    <>
                        <Button variant="secondary" onClick={() => navigate('/home/save-the-date')}>
                            Save the date
                        </Button>
                        <Button variant="primary" onClick={() => navigate('/home/form')}>
                            New event request
                        </Button>
                    </>
                }
            />

            <PageBody className="space-y-6">
                {/* Waiting on you — the only reason to open this tool in a hurry. */}
                <Card>
                    <CardHeader
                        title="Waiting on your approval"
                        description={
                            approvalQueue.length
                                ? `${approvalQueue.length} request${approvalQueue.length === 1 ? '' : 's'} can't move until you sign off.`
                                : undefined
                        }
                        actions={
                            approvalQueue.length > 0 && (
                                <Button size="sm" variant="secondary" onClick={() => navigate('/home/approvals')}>
                                    Review all
                                </Button>
                            )
                        }
                    />
                    {approvalsLoading ? (
                        <EmptyState title="Checking your queue…" />
                    ) : !approvalQueue.length ? (
                        <div className="px-5 py-6 text-sm text-neutral-500">
                            Nothing needs your sign-off right now.
                        </div>
                    ) : (
                        <ul className="divide-y divide-neutral-100">
                            {approvalQueue.slice(0, 4).map((form) => (
                                <li
                                    key={form.id}
                                    className="flex items-center justify-between gap-4 px-5 py-3.5 hover:bg-neutral-50/70"
                                >
                                    <div className="min-w-0">
                                        <p className="truncate text-sm font-medium text-neutral-900">
                                            {form.eventName || 'Untitled event'}
                                        </p>
                                        <p className="mt-0.5 truncate text-xs text-neutral-500">
                                            {formatEventDate(form.date)}
                                            {form.location ? ` · ${form.location}` : ''} · needs{' '}
                                            {form.roles.map(roleLabel).join(' and ')}
                                        </p>
                                    </div>
                                    <Button
                                        size="sm"
                                        variant="primary"
                                        className="flex-shrink-0"
                                        onClick={() => navigate('/home/approvals')}
                                    >
                                        Review
                                    </Button>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>

                {/* Your own requests, still in flight. */}
                <Card>
                    <CardHeader
                        title="Your open requests"
                        description="Drafts, and anything still working through approvals."
                        actions={
                            <Button size="sm" variant="secondary" onClick={() => navigate('/home/request')}>
                                See all
                            </Button>
                        }
                    />
                    {!openRequests.length ? (
                        <div className="px-5 py-6 text-sm text-neutral-500">You have no requests in progress.</div>
                    ) : (
                        <ul className="divide-y divide-neutral-100">
                            {openRequests.map((form) => (
                                <li
                                    key={form.id}
                                    className="flex flex-col gap-2 px-5 py-3.5 hover:bg-neutral-50/70 sm:flex-row sm:items-center sm:justify-between"
                                >
                                    <div className="min-w-0">
                                        <button
                                            type="button"
                                            onClick={() => navigate(`/home/form/${form.id}`)}
                                            className="block max-w-full truncate text-sm font-medium text-neutral-900 hover:text-brand-800"
                                        >
                                            {form.title}
                                        </button>
                                        <p className="mt-0.5 text-xs text-neutral-500">
                                            {formatEventDate(form.eventDate)}
                                            {form.occurrenceCount > 1 && ` + ${form.occurrenceCount - 1} more dates`}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        {form.status !== 'Draft' && (
                                            <div className="hidden md:block">
                                                <ApprovalRail
                                                    required={form.requiredApprovals}
                                                    completed={form.completedApprovals}
                                                />
                                            </div>
                                        )}
                                        <div className="flex flex-shrink-0 items-center gap-2">
                                            <ApprovalCount
                                                required={form.requiredApprovals}
                                                completed={form.completedApprovals}
                                            />
                                            <StatusBadge status={form.status} />
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>

                <Card className="overflow-hidden">
                    <CardHeader title="Campus calendar" description="Approved events." />
                    <div className="p-4">
                        <Calendar viewType="month" />
                    </div>
                </Card>
            </PageBody>
        </>
    );
};
